import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle, AlertCircle, Info, X, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

type ToastVariant = 'success' | 'error' | 'warning' | 'info';

interface Toast {
    id: string;
    title: string;
    description?: string;
    variant: ToastVariant;
    durationMs: number;
}

interface ToastContextValue {
    toast: (opts: Omit<Toast, 'id'>) => void;
    success: (title: string, description?: string) => void;
    error: (title: string, description?: string) => void;
    warning: (title: string, description?: string) => void;
    info: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const ICONS: Record<ToastVariant, React.ElementType> = {
    success: CheckCircle,
    error: AlertCircle,
    warning: AlertTriangle,
    info: Info,
};

const COLORS: Record<ToastVariant, string> = {
    success: 'text-emerald-600 dark:text-emerald-400',
    error: 'text-red-600 dark:text-red-400',
    warning: 'text-amber-600 dark:text-amber-400',
    info: 'text-brand-600 dark:text-brand-400',
};

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
    const Icon = ICONS[toast.variant];
    const progressRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = progressRef.current;
        if (!el) return;
        el.style.transition = `width ${toast.durationMs}ms linear`;
        requestAnimationFrame(() => {
            el.style.width = '0%';
        });
    }, [toast.durationMs]);

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96, transition: { duration: 0.14 } }}
            className={cn(
                'relative w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl',
                'bg-white dark:bg-neutral-800 shadow-xl border border-neutral-200 dark:border-neutral-700',
                'p-4',
            )}
        >
            <div className="flex gap-3">
                <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', COLORS[toast.variant])} />
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 leading-snug">
                        {toast.title}
                    </p>
                    {toast.description && (
                        <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">
                            {toast.description}
                        </p>
                    )}
                </div>
                <button
                    onClick={() => onDismiss(toast.id)}
                    className="ml-2 shrink-0 rounded-md p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
            {/* Progress bar */}
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-neutral-100 dark:bg-neutral-700">
                <div
                    ref={progressRef}
                    className={cn(
                        'h-full w-full',
                        toast.variant === 'success' && 'bg-emerald-500',
                        toast.variant === 'error' && 'bg-red-500',
                        toast.variant === 'warning' && 'bg-amber-500',
                        toast.variant === 'info' && 'bg-brand-500',
                    )}
                />
            </div>
        </motion.div>
    );
}

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const dismiss = useCallback((id: string) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const addToast = useCallback((opts: Omit<Toast, 'id'>) => {
        const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const t: Toast = { ...opts, id };
        setToasts((prev) => [...prev.slice(-3), t]);
        setTimeout(() => dismiss(id), opts.durationMs);
    }, [dismiss]);

    const api: ToastContextValue = {
        toast: addToast,
        success: (title, description) => addToast({ title, description, variant: 'success', durationMs: 4000 }),
        error: (title, description) => addToast({ title, description, variant: 'error', durationMs: 6000 }),
        warning: (title, description) => addToast({ title, description, variant: 'warning', durationMs: 5000 }),
        info: (title, description) => addToast({ title, description, variant: 'info', durationMs: 4000 }),
    };

    return (
        <ToastContext.Provider value={api}>
            {children}
            <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 items-end">
                <AnimatePresence mode="popLayout">
                    {toasts.map((t) => (
                        <ToastCard key={t.id} toast={t} onDismiss={dismiss} />
                    ))}
                </AnimatePresence>
            </div>
        </ToastContext.Provider>
    );
}

export function useToast(): ToastContextValue {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast muss innerhalb von ToastProvider verwendet werden');
    return ctx;
}
